// React imports
import { useState, useMemo } from "react"

// Third-party library imports
import { scaleSymlog, scaleLinear, interpolateWarm } from "d3"
import moment from "moment"
import "moment/locale/fr.js"
import "moment/locale/en-gb.js"
import chroma from "chroma-js"

// Local imports
import Charts from "./components/Chart.js"
import MapVisualization from "./components/MapVisualization.js"
import Minimap from "./components/Minimap.js"

export default function Dataviz({ water, cities }) {
  const [tempHover, setTempHover] = useState([])
  const [lastClick, setLastClick] = useState(false)
  const [scaleChoice, setScaleChoice] = useState("linear")

  const xAxis = "semaine"
  const multipleStation = ["paris"]
  const french = navigator.language === "fr"
  if (french) moment.locale("fr")

  /*
  1. Optimisation : Création d'un dictionnaire pour les stations
  Évite de parcourir le tableau cities à chaque itération du map de water.
  */
  const stationLookup = useMemo(() => {
    const lookup = {}
    cities.forEach((city) => {
      lookup[city.nom.toLowerCase()] = city.commune.toLowerCase()
    })
    return lookup
  }, [cities])

  /*
  2. Optimisation : waterValues avec useMemo et accès direct O(1)
  */
  const waterValues = useMemo(() => {
    return water?.map((week) => {
      const formattedWeek = {}

      Object.entries(week).forEach(([key, value]) => {
        // Nettoyage rapide
        if (value === null || key.includes("__") || key === "date_complet")
          return

        const lowerKey = key.toLowerCase()
        const commune = stationLookup[lowerKey]

        let name = lowerKey

        // Logique de renommage optimisée
        if (lowerKey.includes("national")) {
          name = "national"
        } else if (
          lowerKey !== xAxis &&
          commune &&
          !multipleStation.includes(commune)
        ) {
          name = commune
        }

        // Formatage des valeurs
        let finalValue = value
        if (name === xAxis) {
          finalValue = moment(value.replace("S", ""), "YYYY-WW")
            .add(7, "d")
            .format(french ? "YYYY-DD-MM" : "YYYY-MM-DD")
        } else if (typeof value === "number") {
          finalValue = Math.ceil(value)
        }

        // Fusion National (12 vs 54)
        if (name === "national") {
          formattedWeek[name] = formattedWeek[name] || finalValue
        } else {
          formattedWeek[name] = finalValue
        }
      })
      return formattedWeek
    })
  }, [water, stationLookup, french])

  // Liste des noms de villes extraite des clés du premier objet de données (plus rapide)
  const citiesName = useMemo(() => {
    const names = new Set()
    waterValues.forEach((week) => {
      Object.keys(week).forEach((k) => {
        if (k !== xAxis && k !== "national") names.add(k)
      })
    })
    return ["national", ...Array.from(names).sort()]
  }, [waterValues])

  /*
  States dépendant des données calculées
  */
  const [showCity, setShowCity] = useState(citiesName)
  const [startIndexDataviz, setStartIndexDataviz] = useState(0)
  const [endIndexDataviz, setEndIndexDataviz] = useState(waterValues.length - 1)

  /**
   * Helpers
   */
  const getCityWeek = (city, week = endIndexDataviz) =>
    waterValues[week]?.[city]

  const getUniqueName = ({ commune, nom }) => {
    const c = commune?.toLowerCase()
    const n = nom?.toLowerCase()
    return multipleStation.includes(c) ? n : c
  }

  const filterWaterValue = useMemo(
    () =>
      (weeks = [], filters = []) =>
        weeks.map((week) => {
          const filtered = {}
          Object.entries(week).forEach(([city, value]) => {
            filtered[city] =
              filters.includes(city) || city === xAxis ? value : 0
          })
          return filtered
        }),
    [],
  )

  // Interaction handlers
  const handleMouseOut = (event) => {
    const city =
      event?.currentTarget?.getAttribute("data-unique") ||
      event.value ||
      event.name
    setTempHover((old) => old.filter((name) => name !== city))
    setLastClick(false)
  }

  const handleMouseOver = (event) => {
    const city =
      event?.currentTarget?.getAttribute("data-unique") ||
      event.value ||
      event.name
    setTempHover((old) => [...old, city])
    setLastClick(false)
  }

  const handleclick = (event) => {
    const city =
      event?.currentTarget?.getAttribute("data-unique") ||
      event.value ||
      event.name
    if (showCity.includes(city)) {
      setShowCity((old) => old.filter((name) => name !== city))
    } else {
      setLastClick(city)
      setShowCity((old) => [...old, city])
    }
    setTempHover((old) => old.filter((name) => name !== city))
  }

  const idfCities = [
    "paris",
    "versailles",
    "sarcelles",
    "lagny-sur-marne",
    "evry-courcouronnes",
  ]

  /*
  Color & Scale Management
  */
  const scale = useMemo(
    () =>
      Array.from({ length: 16 }, (_, i) =>
        interpolateWarm(0.1 + (i / 15) * 0.8),
      ).reverse(),
    [],
  )

  const getMax = (weeks) => {
    if (!weeks?.length) return 1000
    let currentMax = 0
    weeks.forEach((week) => {
      Object.entries(week).forEach(([k, v]) => {
        if (k !== xAxis && typeof v === "number" && v > currentMax)
          currentMax = v
      })
    })
    return currentMax || 1000
  }

  const max = useMemo(() => getMax(waterValues), [waterValues])

  const log = scaleSymlog()
    .domain([0, Math.floor(max)])
    .range([0, 16])
    .constant(250)
  const linear = scaleLinear()
    .domain([0, Math.floor(max)])
    .range([0, 16])

  const levelToColor = (level, mode = linear) => {
    const currentScale = mode === log ? log : linear
    const idx = Math.floor(currentScale(Math.floor(level || 0)))
    return scale[idx] || scale[scale.length - 1]
  }

  const lastWorst = useMemo(() => {
    const lastWeek = waterValues[waterValues.length - 1] || {}
    return (
      Object.entries(lastWeek)
        .filter(([k, v]) => k !== xAxis && typeof v === "number")
        .sort(([, a], [, b]) => b - a)?.[0]?.[0] || "national"
    )
  }, [waterValues])

  const initialFilter = useMemo(
    () => [
      ...new Set([
        "national",
        "paris seine-centre",
        "marseille",
        "lyon",
        "nice",
        "toulouse",
        "nantes",
        "montpellier",
        "strasbourg",
        "bordeaux",
        "lille",
        "rennes",
        lastWorst,
      ]),
    ],
    [lastWorst],
  )

  const tempFilter = [...showCity, ...tempHover]
  const filtered = useMemo(
    () => filterWaterValue(waterValues, tempFilter),
    [waterValues, tempFilter, filterWaterValue],
  )

  const citiesAtDate = useMemo(() => {
    const currentWeek = waterValues[endIndexDataviz]
    return cities.filter(
      (city) => currentWeek && currentWeek[getUniqueName(city)],
    )
  }, [cities, waterValues, endIndexDataviz])

  const cityColors = useMemo(
    () =>
      chroma
        .cubehelix()
        .start(0)
        .rotations(0.9)
        .lightness(0.5)
        .gamma(0.5)
        .hue(4)
        .scale()
        .colors(citiesName.length),
    [citiesName],
  )

  return (
    <div className="dashboard">
      <section id="cartography" className="carto">
        <header className="info">
          <time className="date">
            {moment(
              getCityWeek(xAxis),
              french ? "YYYY-DD-MM" : "YYYY-MM-DD",
            ).format("DD MMMM YYYY")}
          </time>

          <div className="counter">
            <span className="city">
              {tempHover?.[0] ||
                lastClick ||
                (french ? "Moyenne nationale" : "National Average")}
            </span>
            <span
              className="concentration"
              style={{
                background: levelToColor(
                  getCityWeek(tempHover?.[0] || lastClick || "national"),
                ),
              }}
            >
              <span>
                {getCityWeek(tempHover?.[0] || lastClick || "national") || 0}
              </span>
            </span>
          </div>
        </header>
        <MapVisualization
          {...{
            french,
            cities: citiesAtDate,
            log,
            scale,
            tempHover,
            showCity,
            levelToColor,
            getCityWeek,
            getUniqueName,
            handleMouseOver,
            handleMouseOut,
            handleclick,
            idfCities,
            waterValues,
            endIndexDataviz,
          }}
        />
      </section>

      <section id="graph" className="graph">
        <div className="preset">
          {french ? "Favoris" : "Presets"}:
          <button
            className={
              "reset " +
              (showCity.length === citiesName.length ? "selected" : "")
            }
            onClick={() => setShowCity(citiesName)}
          >
            {french ? "Toutes les villes" : "All cities"}
          </button>
          <button
            className={
              "reset " +
              (JSON.stringify([...showCity].sort()) ===
              JSON.stringify([...initialFilter].sort())
                ? "selected"
                : "")
            }
            onClick={() => setShowCity(initialFilter)}
          >
            {french ? "Grandes villes" : "Big cities"}
          </button>
          <button
            className={
              "reset " +
              (showCity.length === 1 && showCity[0] === "national"
                ? "selected"
                : "")
            }
            onClick={() => setShowCity(["national"])}
          >
            {french ? "Moyenne Nationale" : "National Average"}
          </button>
        </div>
        <div className="preset">
          {french ? "Période" : "Timerange"}
          <Minimap
            {...{
              filtered,
              citiesName,
              tempFilter,
              cityColors,
              startIndexDataviz,
              endIndexDataviz,
              setEndIndexDataviz,
              setStartIndexDataviz,
              scaleChoice,
              log,
            }}
          />
        </div>
        <Charts
          {...{
            xAxis,
            log,
            getMax,
            filtered,
            citiesName,
            handleMouseOver,
            handleMouseOut,
            handleclick,
            tempFilter,
            showCity,
            tempHover,
            french,
            levelToColor,
            moment,
            startIndexDataviz,
            endIndexDataviz,
            setEndIndexDataviz,
            setStartIndexDataviz,
            cityColors,
            scaleChoice,
            setScaleChoice,
          }}
        />
      </section>
    </div>
  )
}
